import React, { useEffect, useState, useMemo, useRef } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import uuid from "react-uuid";
import {
  DateHelper, FormHelper,
  StyleHelper,
} from "utils";
import { Permisos } from "utils/permisos";
import { CurrencyInput } from "components/PantallasEdit/CurrencyInput/CurrencyInput";
import RadioGroup from "components/RadioGroup/RadioGroup";
import { GridView, ItemLookup, TerceroLookup, SvgComponent, RncLookup } from "components";
import EditHeader from "components/PantallasEdit/Header/EditHeader";
import SectionRow from "components/PantallasEdit/SectionRow/SectionRow";
import EditContainer from "components/PantallasEdit/EditContainer/EditContainer";
import InputContainer from "components/PantallasEdit/InputContainer/InputContainer";
import EditSection from "components/PantallasEdit/EditSection/EditSection";
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import { AnimatedIconButton } from "components/AnimatedIconButton/AnimatedIconButton";
import ModalContainer from "components/ModalContainer/ModalContainer";
import { useAuthentication } from "hooks";
import useMessage from "hooks/useMessage";
import useConfirm from "hooks/useConfirm";
import useExceptionHandler from "hooks/useExceptionHandler";
import useQueryParams from "hooks/useQueryParams";
import { TransaccionesRecurrentesManager, CondicionPagoManager } from "managers";
import {
  eCF,
  TIPO_COMPROBANTE_FACTURA_CREDITO_FISCAL,
  TIPO_COMPROBANTE_FACTURA_CONSUMO,
  MONTO_LIMITE_FACTURA_CONSUMO,
  TIPO_COMPROBANTE_NOTA_DEBITO,
  TIPO_COMPROBANTE_NOTA_CREDITO,
  TIPO_COMPROBANTE_COMPRAS,
  TIPO_COMPROBANTE_GASTO_MENOR,
  TIPO_COMPROBANTE_REGIMEN_ESPECIAL,
  TIPO_COMPROBANTE_GUBERNAMENTAL,
  TIPO_COMPROBANTE_EXPORTACIONES,
  TIPO_COMPROBANTE_PAGOS_EXTERIOR,
  TIPO_PAGO_CREDITO,
  TIPO_PAGO_CONTADO,
  MONEDA_PESO_DOMINICANO_ISO,
  TIPO_DESCUENTO_RECARGO_MONTO,
  TIPO_DESCUENTO_RECARGO_PORCENTAJE,
  TIPO_INGRESOS_OPERACIONES,
  INDICADOR_FACTURACION_NO_FACTURABLE, INDICADOR_FACTURACION_NO_FACTURABLE_LABEL,
  INDICADOR_FACTURACION_ITBIS_TASA_1, INDICADOR_FACTURACION_ITBIS_TASA_1_LABEL,
  INDICADOR_FACTURACION_ITBIS_TASA_2, INDICADOR_FACTURACION_ITBIS_TASA_2_LABEL,
  INDICADOR_FACTURACION_ITBIS_TASA_3, INDICADOR_FACTURACION_ITBIS_TASA_3_LABEL,
  INDICADOR_FACTURACION_ITBIS_EXENTO, INDICADOR_FACTURACION_ITBIS_EXENTO_LABEL,
  INDICADOR_FACTURACION_ITBIS_18_10_PORCIENTO, INDICADOR_FACTURACION_ITBIS_18_10_PORCIENTO_LABEL,
} from "models/ECF/ECF";
import {
  Tercero,
  TIPO_DOCUMENTO_CEDULA,
  TIPO_DOCUMENTO_RNC,
  TIPO_DOCUMENTO_PASAPORTE,
} from "models/ECF/Tercero";
import {
  RETENCIONES_ITBIS_CXC_OPTIONS, RETENCIONES_ITBIS_CXP_OPTIONS,
  RETENCIONES_ISR_CXC_OPTIONS, RETENCIONES_ISR_CXP_OPTIONS,
} from "models/ECF/Retenciones";
import CondicionPagoEditCanvas from "views/CondicionPago/CondicionPagoEditCanvas";

// ─── Constantes de Recurrencia (valores = códigos backend) ────────────────────
const PERIODO_SEMANAL     = "S";
const PERIODO_QUINCENAL   = "Q";
const PERIODO_MENSUAL     = "M";
const PERIODO_TRIMESTRAL  = "T";
const PERIODO_SEMESTRAL   = "6";
const PERIODO_ANUAL       = "A";
const PERIODO_OTRO        = "O";

const PERIODOS = [
  { value: PERIODO_SEMANAL,    label: "Semanal" },
  { value: PERIODO_QUINCENAL,  label: "Quincenal" },
  { value: PERIODO_MENSUAL,    label: "Mensual" },
  { value: PERIODO_TRIMESTRAL, label: "Trimestral" },
  { value: PERIODO_SEMESTRAL,  label: "Semestral" },
  { value: PERIODO_ANUAL,      label: "Anual" },
  { value: PERIODO_OTRO,       label: "Otro" },
];

const DIAS_SEMANA = [
  { value: "Lunes",     label: "Lunes" },
  { value: "Martes",    label: "Martes" },
  { value: "Miércoles", label: "Miércoles" },
  { value: "Jueves",    label: "Jueves" },
  { value: "Viernes",   label: "Viernes" },
  { value: "Sábado",    label: "Sábado" },
  { value: "Domingo",   label: "Domingo" },
];

const QUINCENAS = [
  { value: "Quince",  label: "Quincena 15" },
  { value: "Treinta", label: "Quincena 30" },
];

const DIAS_MES = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}`,
}));

const MESES = [
  { value: "1",  label: "Enero" },
  { value: "2",  label: "Febrero" },
  { value: "3",  label: "Marzo" },
  { value: "4",  label: "Abril" },
  { value: "5",  label: "Mayo" },
  { value: "6",  label: "Junio" },
  { value: "7",  label: "Julio" },
  { value: "8",  label: "Agosto" },
  { value: "9",  label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

const TIPOS_COMPROBANTES_RECURRENTES = eCF.TIPOS_COMPROBANTES.filter(
  (t) => t.value !== TIPO_COMPROBANTE_NOTA_DEBITO && t.value !== TIPO_COMPROBANTE_NOTA_CREDITO
);

function getSecondDropdownLabel(periodo) {
  switch (periodo) {
    case PERIODO_SEMANAL:    return "Día de la semana";
    case PERIODO_QUINCENAL:  return "Quincena";
    case PERIODO_MENSUAL:    return "Día del mes";
    case PERIODO_TRIMESTRAL: return "Mes de inicio";
    case PERIODO_SEMESTRAL:  return "Mes de inicio";
    case PERIODO_ANUAL:      return "Mes";
    default:                 return "";
  }
}
function getSecondDropdownOptions(periodo) {
  switch (periodo) {
    case PERIODO_SEMANAL:    return DIAS_SEMANA;
    case PERIODO_QUINCENAL:  return QUINCENAS;
    case PERIODO_MENSUAL:    return DIAS_MES;
    case PERIODO_TRIMESTRAL: return MESES;
    case PERIODO_SEMESTRAL:  return MESES;
    case PERIODO_ANUAL:      return MESES;
    default:                 return [];
  }
}
function getSecondDropdownValue(recurrencia, periodo) {
  switch (periodo) {
    case PERIODO_SEMANAL:    return recurrencia.DiaSemana;
    case PERIODO_QUINCENAL:  return recurrencia.Quincena;
    case PERIODO_MENSUAL:    return recurrencia.DiaMes;
    case PERIODO_TRIMESTRAL: return recurrencia.MesTrimestreInicio;
    case PERIODO_SEMESTRAL:  return recurrencia.MesSemestreInicio;
    case PERIODO_ANUAL:      return recurrencia.MesAnual;
    default:                 return null;
  }
}
function applySecondDropdownValue(recurrencia, periodo, val) {
  switch (periodo) {
    case PERIODO_SEMANAL:    return { ...recurrencia, DiaSemana: val };
    case PERIODO_QUINCENAL:  return { ...recurrencia, Quincena: val };
    case PERIODO_MENSUAL:    return { ...recurrencia, DiaMes: val };
    case PERIODO_TRIMESTRAL: return { ...recurrencia, MesTrimestreInicio: val };
    case PERIODO_SEMESTRAL:  return { ...recurrencia, MesSemestreInicio: val };
    case PERIODO_ANUAL:      return { ...recurrencia, MesAnual: val };
    default:                 return recurrencia;
  }
}

function MontoField({ value }) {
  return (
    <Form.Control
      type="text"
      readOnly
      value={value}
      style={{ maxWidth: '150px', backgroundColor: '#f8f9fa', fontWeight: 'bold', textAlign: 'right' }}
      tabIndex={-1}
    />
  );
}

export default function TransaccionesRecurrentesEdit({ showLoading, hideLoading }) {
  const history = useHistory();
  const URLParameters = useQueryParams();
  const Id = URLParameters.get("Id");

  const [transaccion, setTransaccion] = useState({});
  const isReadonly = false;
  const { usuario, tienePermiso } = useAuthentication();
  const [detalles, setDetalles] = useState([]);
  const [itemSeleccionado, setItemSeleccionado] = useState({});
  const [descuentoGlobalSeleccionado, setDescuentoGlobalSeleccionado] = useState({});
  const [showSubDescuentoModal, setShowSubDescuentoModal] = useState(false);
  const [indiceDetalleParaDescuento, setIndiceDetalleParaDescuento] = useState(null);
  const [subDescuentoSeleccionado, setSubDescuentoSeleccionado] = useState({});
  const [terceroSeleccionado, setTerceroSeleccionado] = useState({});
  const terceroLookupRef = useRef(null);
  const itemLookupRef = useRef(null);
  const [tituloHeader, setTituloHeader] = useState("Nueva Transacción Recurrente");
  const { Message, Warning, cerrarAlertas } = useMessage();
  const { Confirm } = useConfirm();
  const { handleException } = useExceptionHandler();
  const [pagination, setPagination] = useState({ EsPaginable: false });

  const [condicionesDePago, setCondicionesDePago] = useState([]);
  const [condicionPagoSeleccionada, setCondicionPagoSeleccionada] = useState(null);
  const [itemLookupKey, setItemLookupKey] = useState(uuid());
  const [rncLookupKey, setRncLookupKey] = useState(uuid());
  const [terceroLookupKey, setTerceroLookupKey] = useState(uuid());
  const [showCondicionPagoEditCanvas, setShowCondicionPagoEditCanvas] = useState(false);
  const [showCambiarTipoECFConfirmModal, setShowCambiarTipoECFConfirmModal] = useState(false);
  const [showHistoricoEnviosModal, setShowHistoricoEnviosModal] = useState(false);
  const [historicoEnvios, setHistoricoEnvios] = useState([]);
  const [historicoPagina, setHistoricoPagina] = useState(0);
  const historicoPageSize = 10;
  const [pendingTipoeCF, setPendingTipoeCF] = useState(null);

  const puedeEditarPrecio = tienePermiso(Permisos.Transacciones.EDITAR_PRECIO);
  const puedeEditarITBIS = tienePermiso(Permisos.Transacciones.EDITAR_ITBIS);
  const puedeEditarRetITBIS = tienePermiso(Permisos.Transacciones.EDITAR_RET_ITBIS);
  const puedeEditarRetISR = tienePermiso(Permisos.Transacciones.EDITAR_RET_ISR);
  const puedeEditarAlgunCampoFila =
    puedeEditarPrecio || puedeEditarITBIS || puedeEditarRetITBIS || puedeEditarRetISR;

  const [filasEnEdicion, setFilasEnEdicion] = useState(() => new Set());

  const toggleFilaEnEdicion = (index) => {
    setFilasEnEdicion((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const filaEditable = (index, permiso) =>
    !isReadonly && permiso && filasEnEdicion.has(index);

  // Recurrencia
  const [recurrencia, setRecurrencia] = useState({
    Periodo: null,
    DiaSemana: null, Quincena: null, DiaMes: null,
    MesTrimestreInicio: null, MesSemestreInicio: null, MesAnual: null,
    OtroFechaInicio: null, OtroLapsoDias: null,
    Expira: false, FechaLimite: null,
    ProgramarFechaInicio: false, FechaInicioEjecucion: null,
  });
  const [fechaLimiteKey, setFechaLimiteKey] = useState(uuid());
  const [fechaInicioEjecucionKey, setFechaInicioEjecucionKey] = useState(uuid());
  const [otroFechaInicioKey, setOtroFechaInicioKey] = useState(uuid());

  const esVenta = eCF.TIPO_COMPROBANTE_VENTA_LIST?.some(o => o.value === transaccion.TipoeCF) ?? false;
  const esCompra = eCF.TIPO_COMPROBANTE_COMPRA_LIST?.some(o => o.value === transaccion.TipoeCF) ?? false;

  const tipoTerceroLabel = esVenta
    ? "Cliente"
    : esCompra
      ? "Proveedor"
      : "Cliente | Proveedor";

  const sectionTitleTercero = `Información de ${tipoTerceroLabel}`;
  const documentoTerceroLabel = `Documento ${tipoTerceroLabel}`;
  const razonSocialTerceroLabel = `Razón Social ${tipoTerceroLabel}`;

  const terceroWhereFijo = esVenta
    ? { EsCliente: true }
    : esCompra
      ? { EsProveedor: true }
      : {};

  const itemWhereFijo = esVenta
    ? { EsVendible: true }
    : esCompra
      ? { EsComprable: true }
      : {};

  const CXP_TIPOS = [TIPO_COMPROBANTE_COMPRAS, TIPO_COMPROBANTE_PAGOS_EXTERIOR];
  const itbisOptionsForTipo = CXP_TIPOS.includes(transaccion.TipoeCF)
    ? RETENCIONES_ITBIS_CXP_OPTIONS
    : RETENCIONES_ITBIS_CXC_OPTIONS;
  const isrOptionsForTipo = CXP_TIPOS.includes(transaccion.TipoeCF)
    ? RETENCIONES_ISR_CXP_OPTIONS
    : RETENCIONES_ISR_CXC_OPTIONS;

  const cargarCondicionesDePago = () => {
    CondicionPagoManager
      .listado({ EsPaginable: false, NoEstatus: "I" })
      .then((res) => setCondicionesDePago(res.Data ?? []))
      .catch(() => {});
  };

  // Restaurar condición de pago seleccionada al editar
  useEffect(() => {
    if (transaccion.CondicionPagoId && condicionesDePago.length > 0 && !condicionPagoSeleccionada) {
      const condicion = condicionesDePago.find(c => c.IdSec === transaccion.CondicionPagoId);
      if (condicion) {
        setCondicionPagoSeleccionada({
          value: condicion.IdSec,
          label: condicion.Descripcion,
          Dias: condicion.Dias,
        });
      }
    }
  }, [condicionesDePago, transaccion.CondicionPagoId]);

  useEffect(() => {
    cerrarAlertas();
    cargarCondicionesDePago();
    if (Id) {
      setTituloHeader("Editar Transacción Recurrente");
      showLoading();
      TransaccionesRecurrentesManager.buscarPorId(Id)
        .then((res) => {
          const data = res.Data ?? res;
          const descuentos = data.DescuentosRecargos ?? [];
          const TipoDescuentoGlobal = descuentos.length > 0 ? TIPO_DESCUENTO_RECARGO_MONTO : null;
          const DescuentoGlobal = descuentos.length > 0
            ? descuentos.reduce((sum, d) => sum + (d.MontoDescuentooRecargo ?? 0), 0)
            : null;
          setTransaccion({
            ...data,
            TipoIngresos: data.TipoIngresos != null
              ? String(data.TipoIngresos).padStart(2, "0")
              : data.TipoIngresos,
            DocumentoComprador: data.RNCComprador ?? data.IdentificadorExtranjero,
            TipoDescuentoGlobal,
            DescuentoGlobal,
          });
          if (data.RecurrenciaConfig) {
            setRecurrencia(data.RecurrenciaConfig);
            setFechaLimiteKey(uuid());
            setFechaInicioEjecucionKey(uuid());
            setOtroFechaInicioKey(uuid());
          }
          setDetalles((data.DetallesItem ?? []).map((item) => ({
            ...item,
            Nombre: item.NombreItem,
            Cantidad: item.CantidadItem,
            PrecioUnitario: item.PrecioUnitarioItem,
            IndicadorBienOServicio: item.IndicadorBienoServicio,
          })));
          if (data.RazonSocialComprador) {
            setTerceroSeleccionado({
              value: data.TerceroId,
              label: data.RazonSocialComprador,
              RazonSocial: data.RazonSocialComprador,
              TipoDocumento: data.TipoDocumentoComprador,
              Documento: data.RNCComprador ?? data.IdentificadorExtranjero,
            });
          }
        })
        .catch(handleException)
        .finally(hideLoading);
    } else {
      setDefaultValues();
      hideLoading();
    }
  }, []);

  const setDefaultValues = () => {
    setTransaccion({
      TipoeCF: TIPO_COMPROBANTE_FACTURA_CONSUMO,
      TipoMoneda: MONEDA_PESO_DOMINICANO_ISO,
      FechaEmision: DateHelper.formatDate(new Date()),
      TipoIngresos: TIPO_INGRESOS_OPERACIONES,
      TipoPago: TIPO_PAGO_CONTADO,
    });
  };

  function mapearRecurrenciaADTO(rec) {
    const dto = {
      Programacion: rec.Periodo,
      Expira: rec.Expira || false,
      FechaLimite: rec.Expira ? rec.FechaLimite : null,
      ProgramarFechaInicio: rec.ProgramarFechaInicio || false,
      FechaActivacion: rec.ProgramarFechaInicio ? rec.FechaInicioEjecucion : null,
    };
    switch (rec.Periodo) {
      case PERIODO_SEMANAL:
        dto.OpcionDiaSemana = rec.DiaSemana;
        break;
      case PERIODO_QUINCENAL:
        dto.OpcionQuincena = rec.Quincena;
        break;
      case PERIODO_MENSUAL:
        dto.OpcionDia = rec.DiaMes;
        break;
      case PERIODO_TRIMESTRAL:
        dto.OpcionMes = rec.MesTrimestreInicio;
        dto.OpcionDia = rec.DiaMes;
        break;
      case PERIODO_SEMESTRAL:
        dto.OpcionMes = rec.MesSemestreInicio;
        dto.OpcionDia = rec.DiaMes;
        break;
      case PERIODO_ANUAL:
        dto.OpcionMes = rec.MesAnual;
        dto.OpcionDia = rec.DiaMes;
        break;
      case PERIODO_OTRO:
        dto.OpcionLapsoDias = rec.OtroLapsoDias;
        dto.FechaInicio = rec.OtroFechaInicio;
        break;
      default:
        break;
    }
    return dto;
  }

  function grabar() {
    showLoading();
    const { TipoDescuentoGlobal, DescuentoGlobal, ...transaccionBase } = transaccion;
    const recurrenciaDTO = mapearRecurrenciaADTO(recurrencia);
    const esPasaporte = transaccion.TipoDocumentoComprador === TIPO_DOCUMENTO_PASAPORTE;
    const request = {
      ...transaccionBase,
      ...recurrenciaDTO,
      EnviarCorreo: transaccion.EnviarCorreo || false,
      RNCComprador: esPasaporte ? null : transaccion.DocumentoComprador,
      IdentificadorExtranjero: esPasaporte ? transaccion.DocumentoComprador : null,
      DescuentosRecargos: TipoDescuentoGlobal && DescuentoGlobal
        ? [{
          NumeroLinea: 1,
          TipoAjuste: "D",
          TipoValor: TipoDescuentoGlobal,
          DescripcionDescuentooRecargo: "Descuento",
          ValorDescuentooRecargo: TipoDescuentoGlobal === TIPO_DESCUENTO_RECARGO_PORCENTAJE ? DescuentoGlobal : null,
          MontoDescuentooRecargo: TipoDescuentoGlobal === TIPO_DESCUENTO_RECARGO_MONTO ? DescuentoGlobal : null,
        }]
        : [],
      DetalllesItem: detalles.map((d, index) => ({
        NumeroLinea: index + 1,
        ItemId: d.value,
        NombreItem: d.Nombre,
        IndicadorFacturacion: d.IndicadorFacturacion,
        IndicadorBienoServicio: d.IndicadorBienOServicio,
        PrecioUnitarioItem: d.PrecioUnitario,
        PrecioUnitarioReferencia: d.PrecioUnitarioReferencia,
        CantidadItem: d.Cantidad ?? d.CantidadItem,
        MontoITBISRetenido: d.TotalRetencionItbis ?? d.MontoITBISRetenido ?? 0,
        MontoISRRetenido: d.TotalRetencionIsr ?? d.MontoISRRetenido ?? 0,
        TipoRetencionITBIS: d.TipoRetencionITBIS ?? null,
        TipoRetencionISR: d.TipoRetencionISR ?? null,
        SubDescuentos: d.SubDescuentos || [],
      })),
      RecurrenciaConfig: recurrencia,
    };
    const accion = Id
      ? TransaccionesRecurrentesManager.actualizar(Id, request)
      : TransaccionesRecurrentesManager.crear(request);
    accion
      .then(() => { Message({ message: "Grabado Exitosamente" }); history.push("/TransaccionesRecurrentesList"); })
      .catch((e) => handleException(e))
      .finally(() => hideLoading());
  }

  const handleVolverClick = () => history.push("/TransaccionesRecurrentesList");

  const facturarAhora = () => {
    const { TipoDescuentoGlobal, DescuentoGlobal } = transaccion;
    history.push({
      pathname: "/TransaccionesEdit",
      state: {
        fromRecurrente: true,
        recurrenteId: Id,
        transaccion: {
          TipoeCF: transaccion.TipoeCF,
          TipoMoneda: transaccion.TipoMoneda,
          TipoCambio: transaccion.TipoCambio,
          TipoIngresos: transaccion.TipoIngresos,
          TipoPago: transaccion.TipoPago,
          IndicadorMontoGravado: transaccion.IndicadorMontoGravado,
          FechaEmision: DateHelper.formatDate(new Date()),
          TipoDescuentoGlobal,
          DescuentoGlobal,
          CondicionPagoId: transaccion.CondicionPagoId,
          RNCComprador: transaccion.TipoDocumentoComprador === TIPO_DOCUMENTO_PASAPORTE ? null : transaccion.DocumentoComprador,
          RazonSocialComprador: transaccion.RazonSocialComprador,
          IdentificadorExtranjero: transaccion.TipoDocumentoComprador === TIPO_DOCUMENTO_PASAPORTE ? transaccion.DocumentoComprador : null,
          DocumentoComprador: transaccion.DocumentoComprador,
          TipoDocumentoComprador: transaccion.TipoDocumentoComprador,
          TerceroId: transaccion.TerceroId,
        },
        detalles: detalles.map((d, index) => ({
          ...d,
          NumeroLinea: index + 1,
        })),
        tercero: terceroSeleccionado,
        condicionPago: condicionPagoSeleccionada,
      },
    });
  };

  const reactivarTransaccion = () => {
    if (!Id) return;
    showLoading();
    const { TipoDescuentoGlobal, DescuentoGlobal, ...transaccionBase } = transaccion;
    const recurrenciaDTO = mapearRecurrenciaADTO(recurrencia);
    const esPasaporte = transaccion.TipoDocumentoComprador === TIPO_DOCUMENTO_PASAPORTE;
    const request = {
      ...transaccionBase,
      ...recurrenciaDTO,
      Estatus: "A",
      EnviarCorreo: transaccion.EnviarCorreo || false,
      RNCComprador: esPasaporte ? null : transaccion.DocumentoComprador,
      IdentificadorExtranjero: esPasaporte ? transaccion.DocumentoComprador : null,
      DescuentosRecargos: TipoDescuentoGlobal && DescuentoGlobal
        ? [{
          NumeroLinea: 1,
          TipoAjuste: "D",
          TipoValor: TipoDescuentoGlobal,
          DescripcionDescuentooRecargo: "Descuento",
          ValorDescuentooRecargo: TipoDescuentoGlobal === TIPO_DESCUENTO_RECARGO_PORCENTAJE ? DescuentoGlobal : null,
          MontoDescuentooRecargo: TipoDescuentoGlobal === TIPO_DESCUENTO_RECARGO_MONTO ? DescuentoGlobal : null,
        }]
        : [],
      DetalllesItem: detalles.map((d, index) => ({
        NumeroLinea: index + 1,
        ItemId: d.value,
        NombreItem: d.Nombre,
        IndicadorFacturacion: d.IndicadorFacturacion,
        IndicadorBienoServicio: d.IndicadorBienOServicio,
        PrecioUnitarioItem: d.PrecioUnitario,
        PrecioUnitarioReferencia: d.PrecioUnitarioReferencia,
        CantidadItem: d.Cantidad ?? d.CantidadItem,
        MontoITBISRetenido: d.TotalRetencionItbis ?? d.MontoITBISRetenido ?? 0,
        MontoISRRetenido: d.TotalRetencionIsr ?? d.MontoISRRetenido ?? 0,
        TipoRetencionITBIS: d.TipoRetencionITBIS ?? null,
        TipoRetencionISR: d.TipoRetencionISR ?? null,
        SubDescuentos: d.SubDescuentos || [],
      })),
      RecurrenciaConfig: recurrencia,
    };
    TransaccionesRecurrentesManager.actualizar(Id, request)
      .then(() => {
        Message({ message: "Transacción recurrente reactivada exitosamente." });
        setTransaccion({ ...transaccion, Estatus: "A" });
      })
      .catch(handleException)
      .finally(hideLoading);
  };

  const handleCancelarClick = () => {
    Confirm({
      title: "Cancelar Transacción Recurrente",
      textBody: `¿Está seguro que desea cancelar la transacción recurrente #${Id}?`,
      onConfirm: () => {
        showLoading();
        TransaccionesRecurrentesManager.cancelar(Id)
          .then(() => {
            Message({ message: "Transacción recurrente cancelada exitosamente." });
            setTransaccion({ ...transaccion, Estatus: "C" });
          })
          .catch(handleException)
          .finally(hideLoading);
      },
      onCancel: () => {},
    });
  };

  const cargarHistoricoEnvios = () => {
    showLoading();
    TransaccionesRecurrentesManager.historicoEnvios(Id)
      .then((res) => {
        const data = (res.Data ?? []).map((item) => ({
          ...item,
          FechaCreacionFormatted: DateHelper.formatStringDate(item.FechaCreacion, true),
          EnviadaLabel: item.Enviada ? "Sí" : "No",
        }));
        setHistoricoEnvios(data);
        setHistoricoPagina(0);
        setShowHistoricoEnviosModal(true);
      })
      .catch(handleException)
      .finally(hideLoading);
  };

  const handleEliminarDetalle = (indiceAEliminar) => {
    setDetalles(detalles.filter((_, indice) => indice !== indiceAEliminar));
    setFilasEnEdicion(new Set());
  };

  const getTipoDocumentoPorLongitud = (documento) => {
    const len = (documento || "").length;
    if (len === 0) return "";
    if (len === 9) return TIPO_DOCUMENTO_RNC;
    if (len === 11) return TIPO_DOCUMENTO_CEDULA;
    return TIPO_DOCUMENTO_PASAPORTE;
  };

  const MENSAJE_ITBIS_MIXTO = "Para aplicar descuento global los items deben tener el mismo tipo de ITBIS. Si desea aplicar descuento, debe hacerlo de forma detallada.";
  const MENSAJE_RETENCION_INCOMPATIBLE = "No es posible agregar un descuento general a facturas con retención. Si desea agregar descuento, debe colocarlo de manera detallada.";

  const algunItemTieneRetencion = (lista) =>
    (lista || []).some(d => d.TipoRetencionITBIS || d.TipoRetencionISR);

  const todosMismoIndicador = (lista) => {
    const filtrados = (lista || []).filter(d => d.IndicadorFacturacion !== INDICADOR_FACTURACION_NO_FACTURABLE);
    if (filtrados.length === 0) return true;
    const primero = filtrados[0].IndicadorFacturacion;
    return filtrados.every(d => d.IndicadorFacturacion === primero);
  };

  const calcularLineaConDescuento = (linea, listaDetalles, transaccionRef, itbisOpts, isrOpts) => {
    const cantidad = parseFloat(linea.Cantidad ?? linea.CantidadItem ?? 1) || 0;
    const precio = parseFloat(linea.PrecioUnitario ?? 0) || 0;
    const bruto = precio * cantidad;

    const sub = linea.SubDescuentos?.[0];
    let descLinea = 0;
    if (sub) {
      if (sub.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_MONTO)
        descLinea = sub.MontoSubDescuento || 0;
      else if (sub.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_PORCENTAJE)
        descLinea = ((sub.SubDescuentoPorcentaje || 0) / 100) * bruto;
    }

    let descGlobal = 0;
    const tipoGlobal = transaccionRef?.TipoDescuentoGlobal;
    const valorGlobal = transaccionRef?.DescuentoGlobal || 0;
    if (tipoGlobal === TIPO_DESCUENTO_RECARGO_PORCENTAJE) {
      descGlobal = (valorGlobal / 100) * bruto;
    } else if (tipoGlobal === TIPO_DESCUENTO_RECARGO_MONTO && valorGlobal > 0) {
      const brutoTotalFacturable = (listaDetalles || [])
        .filter(d => d.IndicadorFacturacion !== INDICADOR_FACTURACION_NO_FACTURABLE)
        .reduce((acc, d) => acc + ((parseFloat(d.PrecioUnitario ?? 0) || 0) * (parseFloat(d.Cantidad ?? d.CantidadItem ?? 1) || 0)), 0);
      if (brutoTotalFacturable > 0 && linea.IndicadorFacturacion !== INDICADOR_FACTURACION_NO_FACTURABLE) {
        descGlobal = (bruto / brutoTotalFacturable) * valorGlobal;
      }
    }

    const baseDescontada = Math.max(0, bruto - descLinea - descGlobal);
    const baseRetencion = Math.max(0, bruto - descLinea);

    const tasaITBIS = eCF.INDICADORES_FACTURACION_TASAS[linea.IndicadorFacturacion] ?? 0;
    const itbisLinea = baseDescontada * tasaITBIS;

    const itbisOpt = itbisOpts?.find(o => o.value === linea.TipoRetencionITBIS);
    const isrOpt = isrOpts?.find(o => o.value === linea.TipoRetencionISR);

    return {
      baseDescontada,
      descLinea,
      descGlobal,
      itbisLinea,
      MontoITBISRetenido: itbisOpt ? (baseRetencion * tasaITBIS) * (itbisOpt.valor / 100) : 0,
      MontoISRRetenido: isrOpt ? baseRetencion * (isrOpt.valor / 100) : 0,
    };
  };

  const recalcularRetencionesLinea = (linea, listaDetalles, transaccionRef) => {
    const { MontoITBISRetenido, MontoISRRetenido } = calcularLineaConDescuento(
      linea, listaDetalles, transaccionRef, itbisOptionsForTipo, isrOptionsForTipo
    );
    return { ...linea, MontoITBISRetenido, MontoISRRetenido };
  };

  const recalcularTodasLasRetenciones = (lista, transaccionRef) =>
    lista.map(l => recalcularRetencionesLinea(l, lista, transaccionRef));

  const recalcularRetencionesItemSeleccionado = (item) =>
    recalcularRetencionesLinea(item, [...detalles, item], transaccion);

  const CAMPOS_QUE_AFECTAN_RETENCION = new Set([
    "Cantidad", "PrecioUnitario", "IndicadorFacturacion",
    "TipoRetencionITBIS", "TipoRetencionISR",
  ]);

  const handleDetalleChange = (index, event) => {
    const nuevosDetalles = [...detalles];
    const { name, value } = event.target;
    nuevosDetalles[index] = { ...nuevosDetalles[index], [name]: value };
    if (CAMPOS_QUE_AFECTAN_RETENCION.has(name)) {
      nuevosDetalles[index] = recalcularRetencionesLinea(nuevosDetalles[index], nuevosDetalles, transaccion);
    }
    setDetalles(nuevosDetalles);
  };

  const subtotal = useMemo(() => {
    return detalles
      .filter(item => item.IndicadorFacturacion !== 0)
      .reduce((acc, item) => acc + ((item.PrecioUnitario || 0) * (item.Cantidad || 0)), 0);
  }, [detalles]);

  const totalITBISRetenido = useMemo(() => {
    return detalles
      .filter(item => item.IndicadorFacturacion !== 0 && item.IndicadorFacturacion !== 3 && item.IndicadorFacturacion !== 4)
      .reduce((acc, item) => {
        const { MontoITBISRetenido } = calcularLineaConDescuento(
          item, detalles, transaccion, itbisOptionsForTipo, isrOptionsForTipo
        );
        return acc + (MontoITBISRetenido || 0);
      }, 0);
  }, [detalles, transaccion.TipoDescuentoGlobal, transaccion.DescuentoGlobal]);

  const totalISRRetenido = useMemo(() => {
    return detalles
      .filter(item => item.IndicadorFacturacion !== 0)
      .reduce((acc, item) => {
        const { MontoISRRetenido } = calcularLineaConDescuento(
          item, detalles, transaccion, itbisOptionsForTipo, isrOptionsForTipo
        );
        return acc + (MontoISRRetenido || 0);
      }, 0);
  }, [detalles, transaccion.TipoDescuentoGlobal, transaccion.DescuentoGlobal]);

  const montosDesglosados = useMemo(() => {
    const fmt = (n) => n.toLocaleString('es-DO', { minimumFractionDigits: 2 });
    const sum = (ind) => detalles
      .filter(i => i.IndicadorFacturacion === ind)
      .reduce((acc, i) => acc + (i.PrecioUnitario || 0) * (i.Cantidad || 0), 0);

    const descuentoSum = (ind) => detalles
      .filter(i => i.IndicadorFacturacion === ind)
      .reduce((acc, i) => {
        const sub = i.SubDescuentos?.[0];
        if (!sub) return acc;
        if (sub.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_MONTO)
          return acc + (sub.MontoSubDescuento || 0);
        if (sub.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_PORCENTAJE)
          return acc + (sub.SubDescuentoPorcentaje / 100) * (i.PrecioUnitario || 0) * (i.Cantidad || 0);
        return acc;
      }, 0);

    const base0 = sum(INDICADOR_FACTURACION_NO_FACTURABLE);
    const base1 = sum(INDICADOR_FACTURACION_ITBIS_TASA_1);
    const base2 = sum(INDICADOR_FACTURACION_ITBIS_TASA_2);
    const base3 = sum(INDICADOR_FACTURACION_ITBIS_TASA_3);
    const base4 = sum(INDICADOR_FACTURACION_ITBIS_EXENTO);
    const base5 = sum(INDICADOR_FACTURACION_ITBIS_18_10_PORCIENTO);

    const descuento0 = descuentoSum(INDICADOR_FACTURACION_NO_FACTURABLE);
    const descuento1 = descuentoSum(INDICADOR_FACTURACION_ITBIS_TASA_1);
    const descuento2 = descuentoSum(INDICADOR_FACTURACION_ITBIS_TASA_2);
    const descuento3 = descuentoSum(INDICADOR_FACTURACION_ITBIS_TASA_3);
    const descuento4 = descuentoSum(INDICADOR_FACTURACION_ITBIS_EXENTO);
    const descuento5 = descuentoSum(INDICADOR_FACTURACION_ITBIS_18_10_PORCIENTO);

    const descuentoLineaTotal = descuento1 + descuento2 + descuento3 + descuento4 + descuento5;

    const descuentoGlobalMonto = transaccion.TipoDescuentoGlobal === TIPO_DESCUENTO_RECARGO_PORCENTAJE
      ? ((transaccion.DescuentoGlobal || 0) / 100) * subtotal
      : (transaccion.DescuentoGlobal || 0);

    const descuentoTotalGeneral = descuentoLineaTotal + descuentoGlobalMonto;

    const indicadorGlobal = transaccion.TipoDescuentoGlobal
      ? detalles.find(d => d.IndicadorFacturacion !== INDICADOR_FACTURACION_NO_FACTURABLE)?.IndicadorFacturacion
      : null;
    const globalEn = (ind) => indicadorGlobal === ind ? descuentoGlobalMonto : 0;

    const impuesto1 = (base1 - descuento1 - globalEn(INDICADOR_FACTURACION_ITBIS_TASA_1)) * 0.18;
    const impuesto2 = (base2 - descuento2 - globalEn(INDICADOR_FACTURACION_ITBIS_TASA_2)) * 0.16;
    const impuesto3 = (base3 - descuento3 - globalEn(INDICADOR_FACTURACION_ITBIS_TASA_3)) * 0;
    const impuesto5 = (base5 - descuento5 - globalEn(INDICADOR_FACTURACION_ITBIS_18_10_PORCIENTO)) * 0.10 * 0.18;

    const subtotal1 = (base1 - descuento1 - globalEn(INDICADOR_FACTURACION_ITBIS_TASA_1)) + impuesto1;
    const subtotal2 = (base2 - descuento2 - globalEn(INDICADOR_FACTURACION_ITBIS_TASA_2)) + impuesto2;
    const subtotal3 = (base3 - descuento3 - globalEn(INDICADOR_FACTURACION_ITBIS_TASA_3)) + impuesto3;
    const subtotal4 = (base4 - descuento4 - globalEn(INDICADOR_FACTURACION_ITBIS_EXENTO));
    const subtotal5 = (base5 - descuento5 - globalEn(INDICADOR_FACTURACION_ITBIS_18_10_PORCIENTO)) + impuesto5;

    const impuestoTotal = impuesto1 + impuesto2 + impuesto5;
    const montoTotalConImpuestos = subtotal1 + subtotal2 + subtotal3 + subtotal4 + subtotal5;

    return {
      base0Fmt: fmt(base0),
      base1Fmt: fmt(base1), impuesto1Fmt: fmt(impuesto1), subtotal1Fmt: fmt(subtotal1),
      base2Fmt: fmt(base2), impuesto2Fmt: fmt(impuesto2), subtotal2Fmt: fmt(subtotal2),
      base3Fmt: fmt(base3), impuesto3Fmt: fmt(impuesto3), subtotal3Fmt: fmt(subtotal3),
      base4Fmt: fmt(base4), subtotal4Fmt: fmt(subtotal4),
      base5Fmt: fmt(base5), impuesto5Fmt: fmt(impuesto5), subtotal5Fmt: fmt(subtotal5),
      impuestoTotal,
      impuestoTotalFmt: fmt(impuestoTotal),
      montoTotalConImpuestos,
      montoTotalConImpuestosFmt: fmt(montoTotalConImpuestos),
      descuento0Fmt: fmt(descuento0),
      descuento1Fmt: fmt(descuento1),
      descuento2Fmt: fmt(descuento2),
      descuento3Fmt: fmt(descuento3),
      descuento4Fmt: fmt(descuento4),
      descuento5Fmt: fmt(descuento5),
      descuentoLineaTotalFmt: fmt(descuentoLineaTotal),
      descuentoGlobalFmt: fmt(descuentoGlobalMonto),
      descuentoTotalFmt: fmt(descuentoTotalGeneral),
    };
  }, [detalles, transaccion.TipoDescuentoGlobal, transaccion.DescuentoGlobal, subtotal]);

  const esFacturaConsumo = transaccion.TipoeCF === TIPO_COMPROBANTE_FACTURA_CONSUMO;
  const facturaConsumoSobreLimite =
    esFacturaConsumo &&
    (montosDesglosados.montoTotalConImpuestos ?? 0) >= MONTO_LIMITE_FACTURA_CONSUMO;
  const ocultarTerceroSection = false;

  useEffect(() => {
    if (ocultarTerceroSection && !isReadonly) {
      setTerceroSeleccionado({});
      setTransaccion((prev) => ({
        ...prev,
        TipoDocumentoComprador: "",
        DocumentoComprador: "",
        RazonSocialComprador: "",
      }));
      setTerceroLookupKey(uuid());
      setRncLookupKey(uuid());
    }
  }, [ocultarTerceroSection]);

  const totalAPagar = useMemo(
    () => (montosDesglosados.montoTotalConImpuestos ?? 0) - totalITBISRetenido - totalISRRetenido,
    [montosDesglosados.montoTotalConImpuestos, totalITBISRetenido, totalISRRetenido]
  );

  const itbisCalculado = (itemSeleccionado?.PrecioUnitario || 0)
    * (itemSeleccionado?.Cantidad || 1)
    * (eCF.INDICADORES_FACTURACION_TASAS[itemSeleccionado?.IndicadorFacturacion] ?? 0);

  const handleAbrirSubDescuentoModal = (index) => {
    setIndiceDetalleParaDescuento(index);
    setSubDescuentoSeleccionado({});
    setShowSubDescuentoModal(true);
  };

  const handleCerrarSubDescuentoModal = () => {
    setShowSubDescuentoModal(false);
    setIndiceDetalleParaDescuento(null);
    setSubDescuentoSeleccionado({});
  };

  const handleAgregarSubDescuentoClick = () => {
    if (indiceDetalleParaDescuento === null || !subDescuentoSeleccionado.TipoSubDescuento) return;
    const nuevosDetalles = [...detalles];
    nuevosDetalles[indiceDetalleParaDescuento] = recalcularRetencionesLinea({
      ...nuevosDetalles[indiceDetalleParaDescuento],
      SubDescuentos: [subDescuentoSeleccionado],
    }, nuevosDetalles, transaccion);
    setDetalles(nuevosDetalles);
    handleCerrarSubDescuentoModal();
  };

  const handleEliminarSubDescuento = () => {
    if (indiceDetalleParaDescuento === null) return;
    const tipoSubDescuento = detalles[indiceDetalleParaDescuento]?.SubDescuentos?.[0]?.TipoSubDescuento;
    const nuevosDetalles = [...detalles];
    nuevosDetalles[indiceDetalleParaDescuento] = recalcularRetencionesLinea({
      ...nuevosDetalles[indiceDetalleParaDescuento],
      SubDescuentos: [],
    }, nuevosDetalles, transaccion);
    setDetalles(nuevosDetalles);
    setSubDescuentoSeleccionado({ TipoSubDescuento: tipoSubDescuento });
  };

  const handleAgregarDescuentoClick = () => {
    if (descuentoGlobalSeleccionado.TipoDescuentoGlobal) {
      if (algunItemTieneRetencion(detalles)) {
        Warning({ message: MENSAJE_RETENCION_INCOMPATIBLE });
        return;
      }
      if (!todosMismoIndicador(detalles)) {
        Warning({ message: MENSAJE_ITBIS_MIXTO });
        return;
      }
      const nuevaTransaccion = {
        ...transaccion,
        TipoDescuentoGlobal: descuentoGlobalSeleccionado.TipoDescuentoGlobal,
        DescuentoGlobal: descuentoGlobalSeleccionado.DescuentoGlobal,
      };
      setTransaccion(nuevaTransaccion);
      setDetalles(recalcularTodasLasRetenciones(detalles, nuevaTransaccion));
    }
    setDescuentoGlobalSeleccionado({});
  };

  const handleAgregarItemClick = () => {
    if (itemSeleccionado && itemSeleccionado.value) {
      const cantidadAgregada = itemSeleccionado.Cantidad > 0 ? itemSeleccionado.Cantidad : 1;
      const precio = itemSeleccionado.PrecioUnitario > 0 ? itemSeleccionado.PrecioUnitario : 0.00;
      const nuevoDetalle = {
        ...itemSeleccionado,
        IdSec: itemSeleccionado.value,
        Cantidad: cantidadAgregada,
        CantidadItem: cantidadAgregada,
        PrecioUnitarioReferencia: precio,
        MontoITBISRetenido: itemSeleccionado.MontoITBISRetenido ?? 0,
        MontoISRRetenido: itemSeleccionado.MontoISRRetenido ?? 0,
        SubDescuentos: [],
      };
      const nuevaLista = [...detalles, nuevoDetalle];
      if (transaccion.TipoDescuentoGlobal && !todosMismoIndicador(nuevaLista)) {
        Warning({ message: MENSAJE_ITBIS_MIXTO });
        return;
      }
      setDetalles(recalcularTodasLasRetenciones(nuevaLista, transaccion));
    }
    setItemSeleccionado({});
    setItemLookupKey(uuid());
  };

  // ── Recurrencia ───────────────────────────────────────────────────────────────
  const handlePeriodoChange = (e) => {
    setRecurrencia({
      ...recurrencia, Periodo: e.value,
      DiaSemana: null, Quincena: null, DiaMes: null,
      MesTrimestreInicio: null, MesSemestreInicio: null, MesAnual: null,
      OtroFechaInicio: null, OtroLapsoDias: null,
    });
    setOtroFechaInicioKey(uuid());
  };

  const hasSecondDropdown = recurrencia.Periodo && recurrencia.Periodo !== PERIODO_OTRO;
  const needsDiaMes = recurrencia.Periodo === PERIODO_TRIMESTRAL
    || recurrencia.Periodo === PERIODO_SEMESTRAL
    || recurrencia.Periodo === PERIODO_ANUAL;
  const secondOptions = getSecondDropdownOptions(recurrencia.Periodo);
  const secondValue = getSecondDropdownValue(recurrencia, recurrencia.Periodo);

  const subDescuentoModal = (
    <ModalContainer
      showModal={showSubDescuentoModal}
      cerrar={handleCerrarSubDescuentoModal}
      grabar={() => {}}
      title={`Descuento de Item: ${indiceDetalleParaDescuento !== null ? detalles[indiceDetalleParaDescuento]?.Nombre : ""}`}
      size="lg"
      btnGrabar={{ show: false }}
      btnCerrar={{ show: true, label: "Cerrar" }}
    >
      {indiceDetalleParaDescuento !== null && (() => {
        const descuentoAplicado = detalles[indiceDetalleParaDescuento]?.SubDescuentos?.[0];

        if (descuentoAplicado) {
          return (
            <SectionRow triple={true}>
              <InputContainer stacked label="Tipo de Descuento">
                <RadioGroup
                  options={eCF.TIPOS_DESCUENTOS}
                  selectedOption={descuentoAplicado.TipoSubDescuento}
                  disabled={true}
                />
              </InputContainer>

              <InputContainer stacked label={descuentoAplicado.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_MONTO ? "Monto ($)" : "Porcentaje (%)"}>
                <CurrencyInput
                  readOnly
                  value={descuentoAplicado.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_MONTO
                    ? descuentoAplicado.MontoSubDescuento
                    : descuentoAplicado.SubDescuentoPorcentaje
                  }
                />
              </InputContainer>

              {!isReadonly && (
                <InputContainer stacked label=" ">
                  <IconButton onClick={handleEliminarSubDescuento}>
                    <ToolTip message="Eliminar descuento">
                      <div>
                        <SvgComponent iconName="trash" height="25" width="25" />
                      </div>
                    </ToolTip>
                  </IconButton>
                </InputContainer>
              )}
            </SectionRow>
          );
        }

        const montoOk = subDescuentoSeleccionado.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_MONTO
          && subDescuentoSeleccionado.MontoSubDescuento && (subDescuentoSeleccionado.MontoSubDescuento > 0);
        const porcentajeOk = subDescuentoSeleccionado.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_PORCENTAJE
          && subDescuentoSeleccionado.SubDescuentoPorcentaje && (subDescuentoSeleccionado.SubDescuentoPorcentaje > 0);
        const puedeAgregar = montoOk || porcentajeOk;

        return (
          <SectionRow triple={true}>
            <InputContainer stacked label="Tipo de Descuento">
              <RadioGroup
                options={eCF.TIPOS_DESCUENTOS}
                selectedOption={subDescuentoSeleccionado.TipoSubDescuento}
                onChange={(e) => setSubDescuentoSeleccionado({
                  TipoSubDescuento: e.target.value,
                  MontoSubDescuento: null,
                  SubDescuentoPorcentaje: null,
                })}
              />
            </InputContainer>

            {subDescuentoSeleccionado.TipoSubDescuento && (
              <InputContainer stacked label={subDescuentoSeleccionado.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_MONTO ? "Monto ($)" : "Porcentaje (%)"}>
                {subDescuentoSeleccionado.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_MONTO && (
                  <CurrencyInput
                    name="MontoSubDescuento"
                    value={subDescuentoSeleccionado.MontoSubDescuento ?? ""}
                    onChange={(numericValue) => setSubDescuentoSeleccionado({
                      ...subDescuentoSeleccionado,
                      MontoSubDescuento: numericValue,
                    })}
                  />
                )}

                {subDescuentoSeleccionado.TipoSubDescuento === TIPO_DESCUENTO_RECARGO_PORCENTAJE && (
                  <CurrencyInput
                    name="SubDescuentoPorcentaje"
                    value={subDescuentoSeleccionado.SubDescuentoPorcentaje ?? ""}
                    onChange={(numericValue) => setSubDescuentoSeleccionado({
                      ...subDescuentoSeleccionado,
                      SubDescuentoPorcentaje: numericValue,
                    })}
                  />
                )}
              </InputContainer>
            )}

            {puedeAgregar && (
              <InputContainer stacked label=" ">
                <AnimatedIconButton
                  size="38px"
                  height="20px"
                  width="20px"
                  iconName="create"
                  message="Agregar"
                  onClick={handleAgregarSubDescuentoClick}
                />
              </InputContainer>
            )}
          </SectionRow>
        );
      })()}
    </ModalContainer>
  );

  const cambiarTipoECFConfirmModal = (
    <ModalContainer
      showModal={showCambiarTipoECFConfirmModal}
      cerrar={() => {
        setPendingTipoeCF(null);
        setShowCambiarTipoECFConfirmModal(false);
      }}
      grabar={() => {
        setTransaccion({ ...transaccion, TipoeCF: pendingTipoeCF });
        setPendingTipoeCF(null);
        setShowCambiarTipoECFConfirmModal(false);
      }}
      title={"Confirmación"}
      size="lg"
      btnGrabar={{ show: true, label: "Sí" }}
      btnCerrar={{ show: true, label: "No" }}
    >
      <p>
        El tercero seleccionado tiene un comprobante de tipo{" "}
        <strong>{eCF.TIPOS_COMPROBANTES.find((t) => t.value === pendingTipoeCF)?.label ?? pendingTipoeCF}</strong>
        , pero el tipo de comprobante a emitir es{" "}
        <strong>{eCF.TIPOS_COMPROBANTES.find((t) => t.value === transaccion.TipoeCF)?.label ?? transaccion.TipoeCF}</strong>
        .<br /><br />
        ¿Desea cambiar el tipo de comprobante por{" "}
        <strong>{eCF.TIPOS_COMPROBANTES.find((t) => t.value === pendingTipoeCF)?.label ?? pendingTipoeCF}</strong>?
      </p>
    </ModalContainer>
  );

  const historicoEnviosModal = (
    <ModalContainer
      showModal={showHistoricoEnviosModal}
      cerrar={() => setShowHistoricoEnviosModal(false)}
      grabar={() => {}}
      title="Histórico de Envíos"
      size="xl"
      btnGrabar={{ show: false }}
      btnCerrar={{ show: true, label: "Cerrar" }}
    >
      <GridView
        dataSource={historicoEnvios.slice(historicoPagina * historicoPageSize, (historicoPagina + 1) * historicoPageSize)}
        hideFilters={true}
        allowPaging={true}
        height="auto"
        pageSize={historicoPageSize}
        pageIndex={historicoPagina}
        lastPageNumber={Math.ceil(historicoEnvios.length / historicoPageSize) || 1}
        onPageIndexChanging={(page) => setHistoricoPagina(page - 1)}
      >
        <GridView.Column
          headerText="Fecha de envío"
          dataField="FechaCreacionFormatted"
          sortExpression="FechaCreacion"
          width="180px"
        />
        <GridView.Column
          headerText="Estatus"
          dataField="EstatusDesc"
          width="120px"
          bodyRenderer={(row) => (
            <span style={{
              color: row.Estatus === "C" ? "green" : row.Estatus === "E" ? "red" : "orange",
              fontWeight: "bold",
            }}>
              {row.EstatusDesc}
            </span>
          )}
        />
        <GridView.Column
          headerText="Enviada"
          dataField="EnviadaLabel"
          width="100px"
        />
        <GridView.Column
          headerText={<ToolTip message="Id de transacción generada"><span>Id de Transacción</span></ToolTip>}
          dataField="TransaccionId"
          width="200px"
          number={true}
          bodyRenderer={(row) => (
            <span style={{ float: "right" }}>
              {row.TransaccionId ?? "-"}
            </span>
          )}
        />
        <GridView.Column
          headerText="Mensaje de Error"
          dataField="MensajeError"
          bodyRenderer={(row) => row.MensajeError ?? "-"}
        />
      </GridView>
    </ModalContainer>
  );

  return (
    <>
      <Form action="" className="form-horizontal" method="">
        <Card>
          <EditHeader
            titulo={tituloHeader}
            {...(transaccion.Estatus !== "C" && { grabar: () => grabar() })}
            {...(Id && { historicoEnvios: cargarHistoricoEnvios })}
            {...(Id && transaccion.Estatus === "C" && { reactivar: reactivarTransaccion })}
            {...(Id && transaccion.Estatus !== "C" && { cancelarECF: handleCancelarClick, cancelarECFLabel: "Cancelar" })}
            {...(Id && transaccion.Estatus !== "C" && { facturarAhora })}
            volver={true}
            volverFunc={handleVolverClick}
          />

          {showCondicionPagoEditCanvas && (
            <CondicionPagoEditCanvas
              showCanvas={showCondicionPagoEditCanvas}
              setShowCanvas={setShowCondicionPagoEditCanvas}
              showLoading={showLoading}
              hideLoading={hideLoading}
              updateList={cargarCondicionesDePago}
              onCreated={(res) => {
                const selected = { value: res.IdSec, label: res.Descripcion, Dias: res.Dias };
                setCondicionPagoSeleccionada(selected);
                setTransaccion({ ...transaccion, CondicionPagoId: res.IdSec, FechaLimitePago: null });
              }}
            />
          )}

          <EditContainer disabled={isReadonly}>
            {/* ── Información General ─────────────────────────────────────────── */}
            <EditSection title="Información General" style={{}}>
              <SectionRow multilinea>
                <InputContainer stacked label="Tipo de Comprobante" labelClassName={"d-flex"}>
                  <RadioGroup
                    options={TIPOS_COMPROBANTES_RECURRENTES}
                    selectedOption={transaccion.TipoeCF}
                    disabled={isReadonly}
                    onChange={(e) => {
                      setTransaccion({
                        ...transaccion,
                        TipoeCF: e.target.value,
                      });
                    }}
                  />
                </InputContainer>
              </SectionRow>

              {transaccion.TipoeCF != TIPO_COMPROBANTE_COMPRAS && transaccion.TipoeCF != TIPO_COMPROBANTE_GASTO_MENOR && transaccion.TipoeCF != TIPO_COMPROBANTE_PAGOS_EXTERIOR && (
                <SectionRow multilinea>
                  <InputContainer stacked label="Tipo de Ingresos" labelClassName={"d-flex"}>
                    <RadioGroup
                      options={eCF.TIPOS_INGRESOS}
                      selectedOption={transaccion.TipoIngresos}
                      disabled={isReadonly}
                      onChange={(e) => {
                        setTransaccion({
                          ...transaccion,
                          TipoIngresos: e.target.value,
                        });
                      }}
                    />
                  </InputContainer>
                </SectionRow>
              )}

              <SectionRow columns={4}>
                {transaccion.TipoeCF != TIPO_COMPROBANTE_GASTO_MENOR && transaccion.TipoeCF != TIPO_COMPROBANTE_PAGOS_EXTERIOR && (
                  <InputContainer stacked label="Tipo de Pago">
                    <RadioGroup
                      options={eCF.TIPOS_PAGOS}
                      selectedOption={transaccion.TipoPago}
                      disabled={isReadonly}
                      onChange={(e) => {
                        setTransaccion({
                          ...transaccion,
                          TipoPago: e.target.value,
                          FechaLimitePago: null,
                          CondicionPagoId: null,
                        });
                        setCondicionPagoSeleccionada(null);
                      }}
                    />
                  </InputContainer>
                )}

                {transaccion.TipoeCF != TIPO_COMPROBANTE_GASTO_MENOR && transaccion.TipoeCF != TIPO_COMPROBANTE_PAGOS_EXTERIOR && transaccion.TipoPago === TIPO_PAGO_CREDITO && (
                  <InputContainer
                    stacked
                    label="Condición de Pago"
                    onLabelClick={!isReadonly ? () => setShowCondicionPagoEditCanvas(true) : undefined}
                  >
                    <Select
                      className="react-select primary required"
                      classNamePrefix="react-select"
                      isDisabled={isReadonly}
                      placeholder="Seleccionar condición de pago..."
                      options={condicionesDePago.map((c) => ({
                        value: c.IdSec,
                        label: `${c.Descripcion}`,
                        Dias: c.Dias,
                      }))}
                      value={condicionPagoSeleccionada}
                      onChange={(selected) => {
                        setCondicionPagoSeleccionada(selected);
                        setTransaccion({
                          ...transaccion,
                          CondicionPagoId: selected ? selected.value : null,
                          FechaLimitePago: null,
                        });
                      }}
                    />
                  </InputContainer>
                )}
              </SectionRow>

              <SectionRow columns={4}>
                <InputContainer stacked label="Moneda">
                  <Select
                    className="react-select primary"
                    classNamePrefix="react-select"
                    isDisabled={isReadonly}
                    name="TipoMoneda"
                    options={eCF.MONEDAS}
                    value={eCF.MONEDAS.filter(
                      (option) => option.value === (transaccion.TipoMoneda ?? eCF.MONEDAS[0].value)
                    )}
                    onChange={(e) => {
                      setTransaccion({
                        ...transaccion,
                        TipoMoneda: e.value,
                      });
                    }}
                  />
                </InputContainer>

                {(transaccion.TipoMoneda ?? MONEDA_PESO_DOMINICANO_ISO) !== MONEDA_PESO_DOMINICANO_ISO && (
                  <InputContainer stacked label="Tasa de Cambio">
                    <CurrencyInput
                      name="TipoCambio"
                      placeholder="Tasa de Cambio"
                      value={transaccion.TipoCambio}
                      style={!isReadonly ? StyleHelper.InputRequired : ""}
                      onChange={(numericValue) => {
                        setTransaccion({
                          ...transaccion,
                          TipoCambio: numericValue,
                        });
                      }}
                    />
                  </InputContainer>
                )}

              </SectionRow>
            </EditSection>

            {/* ── Información de Tercero ──────────────────────────────────────── */}
            {transaccion.TipoeCF != TIPO_COMPROBANTE_GASTO_MENOR && !ocultarTerceroSection && (
              <EditSection title={sectionTitleTercero} style={{}}>
                <>
                  <SectionRow columns={4}>
                    {!isReadonly && (
                      <InputContainer stacked label={tipoTerceroLabel} onLabelClick={() => terceroLookupRef.current?.openTerceroEdit()}>
                        <TerceroLookup
                          key={terceroLookupKey}
                          ref={terceroLookupRef}
                          isMultiselect={false}
                          initialValue={terceroSeleccionado}
                          whereFijo={terceroWhereFijo}
                          onChange={(tercero) => {
                            if (tercero && tercero.value) {
                              setTerceroSeleccionado(tercero);

                              const transaccionActualizada = { ...transaccion };
                              transaccionActualizada["TerceroId"] = tercero.value;
                              transaccionActualizada["TipoDocumentoComprador"] = tercero.TipoDocumento;
                              transaccionActualizada["DocumentoComprador"] = tercero.Documento;
                              transaccionActualizada["RazonSocialComprador"] = tercero.RazonSocial;

                              const tipoField = CXP_TIPOS.includes(transaccion.TipoeCF)
                                ? tercero.TipoComprobanteCompra
                                : tercero.TipoComprobanteVenta;
                              var tipoECFTercero = Tercero.getTipoComprobanteECF(tipoField);
                              if (tipoECFTercero) {
                                if (transaccion.TipoeCF != undefined && transaccion.TipoeCF != "" && tipoECFTercero !== transaccion.TipoeCF) {
                                  setPendingTipoeCF(tipoECFTercero);
                                  setShowCambiarTipoECFConfirmModal(true);
                                } else if (!transaccion.TipoeCF || transaccion.TipoeCF === "") {
                                  transaccionActualizada["TipoeCF"] = tipoECFTercero;
                                }
                              }

                              setTransaccion(transaccionActualizada);
                            } else {
                              setTerceroSeleccionado({});
                              setRncLookupKey(uuid());
                              setTransaccion({
                                ...transaccion,
                                TerceroId: null,
                                TipoDocumentoComprador: "",
                                DocumentoComprador: "",
                                RazonSocialComprador: "",
                              });
                            }
                          }}
                        />
                      </InputContainer>
                    )}
                  </SectionRow>

                  <SectionRow columns={4}>
                    <InputContainer stacked label={documentoTerceroLabel}>
                      <RncLookup
                        key={rncLookupKey}
                        disabled={isReadonly}
                        required={!isReadonly && (transaccion.TipoeCF === TIPO_COMPROBANTE_FACTURA_CREDITO_FISCAL ||
                          transaccion.TipoeCF === TIPO_COMPROBANTE_COMPRAS ||
                          transaccion.TipoeCF === TIPO_COMPROBANTE_GUBERNAMENTAL ||
                          facturaConsumoSobreLimite)
                        }
                        initialValue={{
                          value: transaccion.DocumentoComprador ?? "",
                          label: transaccion.DocumentoComprador ?? "",
                          Rnc: transaccion.DocumentoComprador ?? "",
                          RazonSocial: transaccion.RazonSocialComprador ?? "",
                        }}
                        onChange={(e) => {
                          setTransaccion({
                            ...transaccion,
                            DocumentoComprador: e.Rnc,
                            RazonSocialComprador: e.RazonSocial,
                            TipoDocumentoComprador: getTipoDocumentoPorLongitud(e.Rnc),
                          });
                        }}
                      />
                    </InputContainer>

                    <InputContainer stacked label="Tipo Documento">
                      <RadioGroup
                        options={Tercero.TIPO_DOCUMENTO_LIST}
                        selectedOption={transaccion.TipoDocumentoComprador || getTipoDocumentoPorLongitud(transaccion.DocumentoComprador)}
                        disabled={isReadonly}
                        onChange={(e) => {
                          setTransaccion({
                            ...transaccion,
                            TipoDocumentoComprador: e.target.value,
                          });
                        }}
                      />
                    </InputContainer>

                    <InputContainer stacked label={razonSocialTerceroLabel}>
                      <Form.Control
                        type="text"
                        name="RazonSocialComprador"
                        value={transaccion.RazonSocialComprador ?? ""}
                        onChange={(e) => FormHelper.handleChange(e, setTransaccion)}
                        {...(
                          (!isReadonly && (transaccion.TipoeCF === TIPO_COMPROBANTE_FACTURA_CREDITO_FISCAL ||
                            transaccion.TipoeCF === TIPO_COMPROBANTE_COMPRAS ||
                            transaccion.TipoeCF === TIPO_COMPROBANTE_REGIMEN_ESPECIAL ||
                            transaccion.TipoeCF === TIPO_COMPROBANTE_GUBERNAMENTAL ||
                            transaccion.TipoeCF === TIPO_COMPROBANTE_EXPORTACIONES ||
                            facturaConsumoSobreLimite)) &&
                          { style: StyleHelper.InputRequired })
                        }
                      />
                    </InputContainer>
                  </SectionRow>
                </>
              </EditSection>
            )}

            {/* ── Información de Descuento ────────────────────────────────────── */}
            {transaccion.TipoeCF !== TIPO_COMPROBANTE_GASTO_MENOR && transaccion.TipoeCF !== TIPO_COMPROBANTE_PAGOS_EXTERIOR && (
              <EditSection title="Información de Descuento" style={{}}>
                {transaccion.TipoDescuentoGlobal ? (
                  <SectionRow columns={4}>
                    <InputContainer stacked label="Tipo descuento">
                      <RadioGroup
                        options={eCF.TIPOS_DESCUENTOS}
                        disabled={true}
                        selectedOption={transaccion.TipoDescuentoGlobal}
                      />
                    </InputContainer>

                    <InputContainer stacked label="Descuento">
                      <CurrencyInput
                        readOnly
                        value={transaccion.DescuentoGlobal ?? 0}
                        onChange={() => {}}
                      />
                    </InputContainer>

                    {!isReadonly && (
                      <InputContainer>
                        <IconButton
                          onClick={() => {
                            const nuevaTransaccion = {
                              ...transaccion,
                              TipoDescuentoGlobal: null,
                              DescuentoGlobal: null,
                            };
                            setTransaccion(nuevaTransaccion);
                            setDetalles(recalcularTodasLasRetenciones(detalles, nuevaTransaccion));
                            setDescuentoGlobalSeleccionado({});
                          }}
                        >
                          <ToolTip message="Eliminar descuento">
                            <div>
                              <SvgComponent iconName="trash" height="25" width="25" />
                            </div>
                          </ToolTip>
                        </IconButton>
                      </InputContainer>
                    )}
                  </SectionRow>
                ) : (
                  <SectionRow columns={4}>
                    <InputContainer stacked label="Tipo descuento">
                      <RadioGroup
                        options={eCF.TIPOS_DESCUENTOS}
                        selectedOption={descuentoGlobalSeleccionado.TipoDescuentoGlobal}
                        onChange={(e) => {
                          setDescuentoGlobalSeleccionado({
                            ...descuentoGlobalSeleccionado,
                            TipoDescuentoGlobal: e.target.value,
                          });
                        }}
                      />
                    </InputContainer>

                    <InputContainer stacked label="Descuento">
                      <CurrencyInput
                        name="DescuentoGlobal"
                        value={descuentoGlobalSeleccionado.DescuentoGlobal ?? 0}
                        onChange={(numericValue) => {
                          setDescuentoGlobalSeleccionado({
                            ...descuentoGlobalSeleccionado,
                            DescuentoGlobal: numericValue,
                          });
                        }}
                      />
                    </InputContainer>

                    {descuentoGlobalSeleccionado.TipoDescuentoGlobal && descuentoGlobalSeleccionado.DescuentoGlobal > 0 && (
                      <InputContainer>
                        <AnimatedIconButton
                          size="38px"
                          height="20px"
                          width="20px"
                          iconName="create"
                          message="Agregar"
                          onClick={handleAgregarDescuentoClick}
                        />
                      </InputContainer>
                    )}
                  </SectionRow>
                )}
              </EditSection>
            )}

            {/* ── Configuración de Recurrencia ────────────────────────────────── */}
            <EditSection title="Configuración de Recurrencia">
              <SectionRow triple={true}>
                <InputContainer stacked label="Periodo">
                  <Select
                    className="react-select primary required"
                    classNamePrefix="react-select"
                    isDisabled={isReadonly}
                    options={PERIODOS}
                    placeholder="Seleccione un período..."
                    value={PERIODOS.filter(
                      (o) => o.value === recurrencia.Periodo,
                    )}
                    onChange={handlePeriodoChange}
                  />
                </InputContainer>

                {recurrencia.Periodo === PERIODO_OTRO ? (
                  <InputContainer stacked label="Fecha de Inicio">
                    <DateTimePicker
                      key={otroFechaInicioKey}
                      usarLabel={false}
                      esRangoFechas={false}
                      disabled={isReadonly}
                      soloFecha
                      fechaInicioOriginal={recurrencia.OtroFechaInicio || ""}
                      onChange={(e) => {
                        if (!e.fechaInicio) return;
                        setRecurrencia((prev) => ({
                          ...prev,
                          OtroFechaInicio: DateHelper.formatDate(e.fechaInicio),
                        }));
                      }}
                    />
                  </InputContainer>
                ) : hasSecondDropdown ? (
                  <InputContainer
                    stacked
                    label={getSecondDropdownLabel(recurrencia.Periodo)}
                  >
                    <Select
                      className="react-select primary required"
                      classNamePrefix="react-select"
                      isDisabled={isReadonly}
                      key={recurrencia.Periodo}
                      options={secondOptions}
                      placeholder="Seleccione..."
                      value={secondOptions.filter(
                        (o) => o.value === secondValue,
                      )}
                      onChange={(e) =>
                        setRecurrencia(
                          applySecondDropdownValue(
                            recurrencia,
                            recurrencia.Periodo,
                            e.value,
                          ),
                        )
                      }
                    />
                  </InputContainer>
                ) : <div />}

                <div />
              </SectionRow>

              {(needsDiaMes || recurrencia.Periodo === PERIODO_OTRO) && (
                <SectionRow triple={true}>
                  {recurrencia.Periodo === PERIODO_OTRO ? (
                    <InputContainer stacked label="Lapso (días)">
                      <Form.Control
                        type="number"
                        min="1"
                        placeholder="Ej. 30"
                        value={recurrencia.OtroLapsoDias ?? ""}
                        readOnly={isReadonly}
                        onChange={(e) =>
                          setRecurrencia({
                            ...recurrencia,
                            OtroLapsoDias: e.target.value,
                          })
                        }
                      />
                    </InputContainer>
                  ) : (
                    <InputContainer stacked label="Día del mes">
                      <Select
                        className="react-select primary required"
                        classNamePrefix="react-select"
                        isDisabled={isReadonly}
                        options={DIAS_MES}
                        placeholder="Seleccione el día..."
                        value={DIAS_MES.filter(
                          (o) => o.value === recurrencia.DiaMes,
                        )}
                        onChange={(e) =>
                          setRecurrencia({ ...recurrencia, DiaMes: e.value })
                        }
                      />
                    </InputContainer>
                  )}
                  <div />
                  <div />
                </SectionRow>
              )}

              <SectionRow triple={true}>
                <div style={{ paddingTop: "8px" }}>
                  <Form.Check
                    type="checkbox"
                    id="chk-expira"
                    disabled={isReadonly}
                    checked={!!recurrencia.Expira}
                    label="Expira?"
                    onChange={(e) => {
                      setRecurrencia({
                        ...recurrencia,
                        Expira: e.target.checked,
                        FechaLimite: null,
                      });
                      setFechaLimiteKey(uuid());
                    }}
                  />
                </div>
                {recurrencia.Expira && (
                  <InputContainer stacked label="Fecha límite">
                    <DateTimePicker
                      key={fechaLimiteKey}
                      usarLabel={false}
                      esRangoFechas={false}
                      disabled={isReadonly}
                      soloFecha
                      fechaInicioOriginal={recurrencia.FechaLimite || ""}
                      onChange={(e) => {
                        if (!e.fechaInicio) return;
                        setRecurrencia((prev) => ({
                          ...prev,
                          FechaLimite: DateHelper.formatDate(e.fechaInicio),
                        }));
                      }}
                    />
                  </InputContainer>
                )}
              </SectionRow>

              <SectionRow triple={true}>
                <div style={{ paddingTop: "8px" }}>
                  <Form.Check
                    type="checkbox"
                    id="chk-programar-inicio"
                    disabled={isReadonly}
                    checked={!!recurrencia.ProgramarFechaInicio}
                    label="Programar Inicio"
                    onChange={(e) => {
                      setRecurrencia({
                        ...recurrencia,
                        ProgramarFechaInicio: e.target.checked,
                        FechaInicioEjecucion: null,
                      });
                      setFechaInicioEjecucionKey(uuid());
                    }}
                  />
                </div>
                {recurrencia.ProgramarFechaInicio && (
                  <InputContainer stacked label="Fecha de inicio">
                    <DateTimePicker
                      key={fechaInicioEjecucionKey}
                      usarLabel={false}
                      esRangoFechas={false}
                      disabled={isReadonly}
                      soloFecha
                      fechaInicioOriginal={recurrencia.FechaInicioEjecucion || ""}
                      onChange={(e) => {
                        if (!e.fechaInicio) return;
                        setRecurrencia((prev) => ({
                          ...prev,
                          FechaInicioEjecucion: DateHelper.formatDate(e.fechaInicio),
                        }));
                      }}
                    />
                  </InputContainer>
                )}
              </SectionRow>
              {!usuario?.Compania?.EnviarRepresentacionImpresaAutomatica && (
                <SectionRow triple={true}>
                  <div style={{ paddingTop: "8px" }}>
                    <Form.Check
                      type="checkbox"
                      id="chk-enviar-correo"
                      disabled={isReadonly}
                      checked={!!transaccion.EnviarCorreo}
                      label="Enviar Representacion Impresa por Email"
                      onChange={(e) => {
                        setTransaccion({
                          ...transaccion,
                          EnviarCorreo: e.target.checked,
                        });
                      }}
                    />
                  </div>
                </SectionRow>
              )}
            </EditSection>

            {/* ── Detalles de la Transacción ──────────────────────────────────── */}
            <EditSection title="Detalles de la Transacción" mt="mt-4">
              <Row className="align-items-end">
                {!isReadonly && (
                  <Col md={2}>
                    <InputContainer stacked label="Item" onLabelClick={() => itemLookupRef.current?.openItemEdit()}>
                      <ItemLookup
                        ref={itemLookupRef}
                        key={itemLookupKey}
                        isMultiselect={false}
                        initialValue={itemSeleccionado}
                        whereFijo={itemWhereFijo}
                        onChange={(item) => {
                          setItemSeleccionado(item);
                        }}
                      />
                    </InputContainer>
                  </Col>
                )}

                {itemSeleccionado && itemSeleccionado.value && (
                  <Col md={1}>
                    <InputContainer stacked label="Cantidad">
                      <Form.Control
                        type="number"
                        name="Cantidad"
                        placeholder="Cantidad"
                        value={itemSeleccionado.Cantidad ?? 1}
                        onChange={(e) => {
                          setItemSeleccionado(recalcularRetencionesItemSeleccionado({
                            ...itemSeleccionado,
                            Cantidad: e.target.value,
                          }));
                        }}
                      />
                    </InputContainer>
                  </Col>
                )}

                {itemSeleccionado && itemSeleccionado.value && (
                  <Col md={1.5}>
                    <InputContainer stacked label="Precio unitario">
                      <CurrencyInput
                        name="PrecioUnitario"
                        placeholder="Precio Unitario"
                        value={itemSeleccionado.PrecioUnitario || 0}
                        onChange={(numericValue) => {
                          setItemSeleccionado(recalcularRetencionesItemSeleccionado({
                            ...itemSeleccionado,
                            PrecioUnitario: numericValue,
                          }));
                        }}
                      />
                    </InputContainer>
                  </Col>
                )}

                {itemSeleccionado && itemSeleccionado.value && (
                  <Col md={2}>
                    <InputContainer stacked label="Tasa ITBIS">
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        options={eCF.INDICADORES_FACTURACION}
                        value={eCF.INDICADORES_FACTURACION.find(opt => opt.value === itemSeleccionado.IndicadorFacturacion) ?? null}
                        onChange={(sel) => {
                          const nuevoValor = sel ? sel.value : null;
                          if (transaccion.TipoDescuentoGlobal && nuevoValor !== INDICADOR_FACTURACION_NO_FACTURABLE) {
                            const otros = detalles.filter(d => d.IndicadorFacturacion !== INDICADOR_FACTURACION_NO_FACTURABLE);
                            if (otros.length > 0 && otros[0].IndicadorFacturacion !== nuevoValor) {
                              Warning({ message: MENSAJE_ITBIS_MIXTO });
                              return;
                            }
                          }
                          setItemSeleccionado({
                            ...itemSeleccionado,
                            IndicadorFacturacion: nuevoValor,
                          });
                        }}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      <CurrencyInput value={itbisCalculado} readOnly />
                    </InputContainer>
                  </Col>
                )}

                {itemSeleccionado && itemSeleccionado.value && (
                  <Col md={2}>
                    <InputContainer stacked label="Retención ITBIS">
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        isClearable
                        options={itbisOptionsForTipo}
                        value={itbisOptionsForTipo.find(opt => opt.value === itemSeleccionado.TipoRetencionITBIS) ?? null}
                        onChange={(sel) => {
                          if (sel && transaccion.TipoDescuentoGlobal) {
                            Warning({ message: MENSAJE_RETENCION_INCOMPATIBLE });
                            return;
                          }
                          setItemSeleccionado(recalcularRetencionesItemSeleccionado({
                            ...itemSeleccionado,
                            TipoRetencionITBIS: sel ? sel.value : null,
                          }));
                        }}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      <CurrencyInput value={itemSeleccionado.MontoITBISRetenido || 0} readOnly />
                    </InputContainer>
                  </Col>
                )}

                {itemSeleccionado && itemSeleccionado.value && (
                  <Col md={2}>
                    <InputContainer stacked label="Retención ISR">
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        isClearable
                        options={isrOptionsForTipo}
                        value={isrOptionsForTipo.find(opt => opt.value === itemSeleccionado.TipoRetencionISR) ?? null}
                        onChange={(sel) => {
                          if (sel && transaccion.TipoDescuentoGlobal) {
                            Warning({ message: MENSAJE_RETENCION_INCOMPATIBLE });
                            return;
                          }
                          setItemSeleccionado(recalcularRetencionesItemSeleccionado({
                            ...itemSeleccionado,
                            TipoRetencionISR: sel ? sel.value : null,
                          }));
                        }}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      <CurrencyInput value={itemSeleccionado.MontoISRRetenido || 0} readOnly />
                    </InputContainer>
                  </Col>
                )}

                {itemSeleccionado && itemSeleccionado.value && (
                  <Col md={1}>
                    <InputContainer>
                      <AnimatedIconButton
                        size="38px"
                        height="20px"
                        width="20px"
                        iconName="create"
                        message="Agregar"
                        onClick={handleAgregarItemClick}
                      />
                    </InputContainer>
                  </Col>
                )}
              </Row>

              <GridView
                pagination={pagination}
                setPagination={setPagination}
                dataSource={detalles}
                hideFilters={true}
                height="auto"
              >
                <GridView.Column
                  isField
                  width="120px"
                  showClearFilter={true}
                  bodyRenderer={(row) => {
                    const index = detalles.findIndex(item => item === row);
                    const count = row.SubDescuentos?.length || 0;
                    return (
                      <>
                        {!isReadonly && (
                          <div className="float-right">
                            <IconButton
                              onClick={() => {
                                const indiceEncontrado = detalles.findIndex(item => item === row);
                                handleEliminarDetalle(indiceEncontrado);
                              }}
                            >
                              <ToolTip message="Eliminar">
                                <div>
                                  <SvgComponent iconName="trash" height="25" width="25" />
                                </div>
                              </ToolTip>
                            </IconButton>
                          </div>
                        )}

                        {(!isReadonly || count > 0) && (
                          <div className="float-right">
                            <div onClick={() => handleAbrirSubDescuentoModal(index)} style={{ cursor: "pointer" }}>
                              <ToolTip message={"Descuento detallado"}>
                                <div style={{ position: "relative" }}>
                                  <SvgComponent iconName="descuento" height="25" width="25" />
                                  {count > 0 && (
                                    <span style={{
                                      position: "absolute", top: "0px", right: "-5px",
                                      background: "red", color: "white", borderRadius: "50%",
                                      fontSize: "10px", width: "16px", height: "16px",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                      {count}
                                    </span>
                                  )}
                                </div>
                              </ToolTip>
                            </div>
                          </div>
                        )}

                        {!isReadonly && puedeEditarAlgunCampoFila && (
                          <div className="float-right">
                            <IconButton onClick={() => toggleFilaEnEdicion(index)}>
                              <ToolTip message={filasEnEdicion.has(index) ? "Bloquear fila" : "Editar fila"}>
                                <div>
                                  <SvgComponent
                                    iconName={filasEnEdicion.has(index) ? "cancel" : "pencil"}
                                    height="25"
                                    width="25"
                                  />
                                </div>
                              </ToolTip>
                            </IconButton>
                          </div>
                        )}
                      </>
                    );
                  }}
                />
                <GridView.Column
                  headerText="#"
                  width="50px"
                  bodyRenderer={(row) => (
                    <div style={{ textAlign: 'center' }}>
                      {detalles.findIndex(item => item === row) + 1}
                    </div>
                  )}
                />
                <GridView.Column
                  headerText="Nombre"
                  dataField="Nombre"
                />
                <GridView.Column
                  headerText="Bien o Servicio"
                  bodyRenderer={(row) => {
                    const index = detalles.findIndex(item => item === row);
                    return (
                      <div style={{ textAlign: 'left', paddingLeft: '8px', width: '150px' }}>
                        <Select
                          className="react-select primary"
                          classNamePrefix="react-select"
                          isDisabled={isReadonly}
                          name="IndicadorBienOServicio"
                          options={eCF.INDICADOR_BIEN_SERVICIO}
                          value={eCF.INDICADOR_BIEN_SERVICIO.find(opt => opt.value === row.IndicadorBienOServicio)}
                          onChange={(selectedOption) => {
                            handleDetalleChange(index, {
                              target: {
                                name: "IndicadorBienOServicio",
                                value: selectedOption ? selectedOption.value : "",
                              }
                            });
                          }}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                    );
                  }}
                />
                <GridView.Column
                  headerText="Cantidad"
                  bodyRenderer={(row) => {
                    const index = detalles.findIndex(item => item === row);
                    return (
                      <div style={{ textAlign: 'left', paddingLeft: '8px' }}>
                        <Form.Control
                          type="number"
                          name="Cantidad"
                          value={row.Cantidad}
                          onChange={(event) => handleDetalleChange(index, event)}
                          style={{ maxWidth: '110px' }}
                          min="1"
                        />
                      </div>
                    );
                  }}
                />
                <GridView.Column
                  headerText="Precio Unitario"
                  bodyRenderer={(row) => {
                    const index = detalles.findIndex(item => item === row);
                    const editable = filaEditable(index, puedeEditarPrecio);
                    return (
                      <CurrencyInput
                        name="PrecioUnitario"
                        value={row.PrecioUnitario || 0}
                        readOnly={!editable}
                        onChange={(numericValue) =>
                          handleDetalleChange(index, { target: { name: "PrecioUnitario", value: numericValue } })
                        }
                      />
                    );
                  }}
                />
                <GridView.Column
                  headerText="ITBIS"
                  bodyRenderer={(row) => {
                    const index = detalles.findIndex(item => item === row);
                    const editable = filaEditable(index, puedeEditarITBIS);
                    const { itbisLinea: itbis } = calcularLineaConDescuento(
                      row, detalles, transaccion, itbisOptionsForTipo, isrOptionsForTipo
                    );
                    if (!editable) {
                      return (
                        <div style={{ textAlign: 'left', paddingLeft: '8px' }}>
                          <CurrencyInput value={itbis} readOnly />
                        </div>
                      );
                    }
                    return (
                      <div style={{ textAlign: 'left', paddingLeft: '8px' }}>
                        <Select
                          className="react-select primary"
                          classNamePrefix="react-select"
                          name="IndicadorFacturacion"
                          options={eCF.INDICADORES_FACTURACION}
                          value={eCF.INDICADORES_FACTURACION.find(opt => opt.value === row.IndicadorFacturacion)}
                          onChange={(selectedOption) => {
                            const nuevoValor = selectedOption ? selectedOption.value : "";
                            if (transaccion.TipoDescuentoGlobal && nuevoValor !== "" && nuevoValor !== INDICADOR_FACTURACION_NO_FACTURABLE) {
                              const simulada = detalles.map((d, i) => i === index ? { ...d, IndicadorFacturacion: nuevoValor } : d);
                              if (!todosMismoIndicador(simulada)) {
                                Warning({ message: MENSAJE_ITBIS_MIXTO });
                                return;
                              }
                            }
                            handleDetalleChange(index, {
                              target: {
                                name: "IndicadorFacturacion",
                                value: nuevoValor,
                              }
                            });
                          }}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                        <CurrencyInput value={itbis} readOnly />
                      </div>
                    );
                  }}
                />
                <GridView.Column
                  headerText="Retención ITBIS"
                  bodyRenderer={(row) => {
                    const index = detalles.findIndex(item => item === row);
                    const editable = filaEditable(index, puedeEditarRetITBIS);
                    if (!editable) {
                      return (
                        <div style={{ minWidth: '200px' }}>
                          <CurrencyInput
                            readOnly={true}
                            name="MontoITBISRetenido"
                            value={row.MontoITBISRetenido || 0}
                          />
                        </div>
                      );
                    }
                    return (
                      <div style={{ minWidth: '200px' }}>
                        <Select
                          className="react-select primary"
                          classNamePrefix="react-select"
                          isClearable
                          options={itbisOptionsForTipo}
                          value={itbisOptionsForTipo.find(opt => opt.value === row.TipoRetencionITBIS) ?? null}
                          onChange={(sel) => {
                            if (sel && transaccion.TipoDescuentoGlobal) {
                              Warning({ message: MENSAJE_RETENCION_INCOMPATIBLE });
                              return;
                            }
                            const nuevosDetalles = [...detalles];
                            const lineaActualizada = { ...nuevosDetalles[index], TipoRetencionITBIS: sel ? sel.value : null };
                            nuevosDetalles[index] = recalcularRetencionesLinea(lineaActualizada, nuevosDetalles, transaccion);
                            setDetalles(nuevosDetalles);
                          }}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                        <CurrencyInput
                          readOnly={true}
                          name="MontoITBISRetenido"
                          value={row.MontoITBISRetenido || 0}
                          onChange={(numericValue) =>
                            handleDetalleChange(index, { target: { name: "MontoITBISRetenido", value: numericValue } })
                          }
                        />
                      </div>
                    );
                  }}
                />
                <GridView.Column
                  headerText="Retención ISR"
                  bodyRenderer={(row) => {
                    const index = detalles.findIndex(item => item === row);
                    const editable = filaEditable(index, puedeEditarRetISR);
                    if (!editable) {
                      return (
                        <div style={{ minWidth: '200px' }}>
                          <CurrencyInput
                            readOnly={true}
                            name="MontoISRRetenido"
                            value={row.MontoISRRetenido || 0}
                          />
                        </div>
                      );
                    }
                    return (
                      <div style={{ minWidth: '200px' }}>
                        <Select
                          className="react-select primary"
                          classNamePrefix="react-select"
                          isClearable
                          options={isrOptionsForTipo}
                          value={isrOptionsForTipo.find(opt => opt.value === row.TipoRetencionISR) ?? null}
                          onChange={(sel) => {
                            if (sel && transaccion.TipoDescuentoGlobal) {
                              Warning({ message: MENSAJE_RETENCION_INCOMPATIBLE });
                              return;
                            }
                            const nuevosDetalles = [...detalles];
                            const lineaActualizada = { ...nuevosDetalles[index], TipoRetencionISR: sel ? sel.value : null };
                            nuevosDetalles[index] = recalcularRetencionesLinea(lineaActualizada, nuevosDetalles, transaccion);
                            setDetalles(nuevosDetalles);
                          }}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                        <CurrencyInput
                          readOnly={true}
                          name="MontoISRRetenido"
                          value={row.MontoISRRetenido || 0}
                          onChange={(numericValue) =>
                            handleDetalleChange(index, { target: { name: "MontoISRRetenido", value: numericValue } })
                          }
                        />
                      </div>
                    );
                  }}
                />
              </GridView>
            </EditSection>

            {/* ── Totales ────────────────────────────────────────────────────── */}
            {detalles.length > 0 && (
              <EditSection title="Totales" style={{ textAlign: 'right', marginRight: '10px' }}>
                <Row>
                  <Col md={{ span: 4, offset: 8 }} className="d-flex flex-column align-items-end">
                    <InputContainer stacked label="SubTotal">
                      <MontoField value={subtotal.toLocaleString('es-DO', { minimumFractionDigits: 2 })} />
                    </InputContainer>

                    {montosDesglosados.descuentoTotalFmt > 0 && (
                      <InputContainer stacked label="Descuento">
                        <MontoField value={montosDesglosados.descuentoTotalFmt} />
                      </InputContainer>
                    )}

                    <InputContainer stacked label="ITBIS">
                      <MontoField value={montosDesglosados.impuestoTotalFmt} />
                    </InputContainer>

                    {totalITBISRetenido > 0 && (
                      <InputContainer stacked label="Retención ITBIS">
                        <MontoField value={totalITBISRetenido.toLocaleString('es-DO', { minimumFractionDigits: 2 })} />
                      </InputContainer>
                    )}

                    {totalISRRetenido > 0 && (
                      <InputContainer stacked label="Retención ISR">
                        <MontoField value={totalISRRetenido.toLocaleString('es-DO', { minimumFractionDigits: 2 })} />
                      </InputContainer>
                    )}

                    <InputContainer stacked label="Total">
                      <MontoField value={montosDesglosados.montoTotalConImpuestosFmt} />
                    </InputContainer>

                  </Col>
                </Row>
              </EditSection>
            )}
          </EditContainer>
        </Card>
      </Form>

      {showSubDescuentoModal && subDescuentoModal}

      {showCambiarTipoECFConfirmModal && cambiarTipoECFConfirmModal}

      {showHistoricoEnviosModal && historicoEnviosModal}
    </>
  );
}
